import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { MenuData, MenuItem, CartItem, SelectedOption, ChoiceGroup, validateItem } from '../types';
import { 
  ShoppingBag, X, Plus, Minus, ChevronLeft, ChevronRight, ChevronDown, Check, AlertCircle, 
  Search, EyeOff, Eye, Edit2, RotateCcw, FileSpreadsheet, GripVertical, Star, Clock, MapPin, CreditCard, Info, MessageSquare
} from 'lucide-react';
import { 
  DndContext, 
  closestCenter, 
  KeyboardSensor, 
  PointerSensor, 
  useSensor, 
  useSensors, 
  DragEndEvent 
} from '@dnd-kit/core';
import { 
  arrayMove, 
  SortableContext, 
  sortableKeyboardCoordinates, 
  useSortable, 
  rectSortingStrategy,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { exportTemplate1, exportTemplate2, exportTemplateBoth } from '../services/excelService';
import { downloadImagesZip } from '../services/imageService';
import { ExportModal } from './ExportModal';

const DeleteConfirmButton = ({ 
    onDelete, 
    icon = <X size={16} />, 
    className = "",
    title = "Delete"
}: { 
    onDelete: () => void, 
    icon?: React.ReactNode, 
    className?: string,
    title?: string
}) => {
    const [isConfirming, setIsConfirming] = useState(false);

    if (isConfirming) {
        return (
            <div 
                className={`${className} !p-0.5 !bg-white !border !border-slate-200 !rounded flex items-center gap-1 animate-in fade-in zoom-in duration-200 shadow-sm z-20`} 
                onClick={e => e.stopPropagation()}
                style={{ width: 'auto', height: 'auto', minWidth: 'fit-content' }}
            >
                <button 
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onDelete(); setIsConfirming(false); }}
                    className="p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                    title="Confirm Delete"
                >
                    <Check size={14} strokeWidth={3} />
                </button>
                <button 
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setIsConfirming(false); }}
                    className="p-1 text-slate-400 hover:bg-slate-100 rounded transition-colors"
                    title="Cancel"
                >
                    <X size={14} strokeWidth={3} />
                </button>
            </div>
        );
    }

    return (
        <button 
            type="button"
            onClick={(e) => { e.stopPropagation(); setIsConfirming(true); }}
            className={className}
            title={title}
        >
            {icon}
        </button>
    );
};

interface OrderingPageProps {
  data: MenuData;
  onBack: () => void;
  onEditItem?: (itemId: string) => void;
  currency: string;
  language: 'en' | 'ar';
  viewFilters?: {
    showImagesOnly: boolean;
    showNotesOnly: boolean;
    showIncompleteOnly: boolean;
  };
  onOpenImageEditor?: () => void;
}

export interface OrderingPageRef {
    reset: () => void;
    exportLive: () => void;
}

// Sortable Category Component
const SortableCategory = ({ category, children, getText, isArabic, isExpanded, onToggle }: any) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: category.id, data: { type: 'category' } });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 40 : 'auto',
    };

    return (
        <div ref={setNodeRef} style={style} id={`menu-cat-${category.id}`} className="scroll-mt-24">
            <div 
                className="flex items-center justify-between bg-slate-100 p-3 rounded-lg mb-4 transition-colors group select-none"
            >
                <div className="flex items-center gap-3 flex-1">
                    <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 text-slate-400 hover:text-slate-600">
                        <GripVertical size={18} />
                    </div>
                    <div className="flex-1 cursor-pointer" onClick={onToggle}>
                        <h3 className="font-bold text-slate-800 text-lg flex items-center gap-2">
                            {getText(category.name)} <span role="img" aria-label="fire">🔥</span>
                        </h3>
                    </div>
                </div>
                <button onClick={onToggle} className="p-1 text-slate-400 hover:text-slate-600">
                    {isExpanded ? <ChevronDown size={20} /> : (isArabic ? <ChevronLeft size={20} /> : <ChevronRight size={20} />)}
                </button>
            </div>
            {isExpanded && children}
        </div>
    );
};

// Sortable Item Component
const SortableItemCard = ({ item, getText, currency, isArabic, onSelect, onEdit, onToggleHide, isHidden, showIncompleteOnly }: any) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: item.id, data: { type: 'item' } });

  const validationErrors = validateItem(item);
  const isIncomplete = validationErrors.length > 0;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : (isHidden ? 0.5 : 1),
    filter: isHidden ? 'grayscale(100%)' : 'none',
    zIndex: isDragging ? 50 : 'auto',
  };

  return (
    <div 
        ref={setNodeRef} 
        style={style} 
        className={`bg-white border border-slate-200 rounded-lg p-4 flex gap-4 hover:shadow-md transition-shadow group relative ${isHidden ? 'bg-slate-50' : ''} ${showIncompleteOnly && isIncomplete ? 'bg-red-50/50 border-red-200' : ''}`}
    >
        {/* Validation Errors Overlay */}
        {showIncompleteOnly && isIncomplete && (
            <div className="absolute -top-2 left-1/2 -translate-x-1/2 bg-red-100 text-red-700 text-[10px] font-bold px-2 py-0.5 rounded-full border border-red-200 shadow-sm z-20 whitespace-nowrap flex items-center gap-1">
                <AlertCircle size={10} />
                <span>Missing: {validationErrors.join(', ')}</span>
            </div>
        )}

        {/* Drag Handle */}
        <div {...attributes} {...listeners} className="absolute top-2 left-2 p-1 text-slate-300 hover:text-slate-600 cursor-grab active:cursor-grabbing z-10">
            <GripVertical size={16} />
        </div>

        {/* Controls */}
        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
            <button onClick={() => onEdit(item.id)} className="p-1.5 bg-blue-50 text-blue-600 rounded hover:bg-blue-100" title="Edit in Editor">
                <Edit2 size={14} />
            </button>
            <button 
                onClick={() => onToggleHide(item.id)} 
                className={`p-1.5 rounded transition-colors ${isHidden ? 'bg-slate-200 text-slate-600 hover:bg-slate-300' : 'bg-red-50 text-red-600 hover:bg-red-100'}`} 
                title={isHidden ? "Unhide Item" : "Hide Item"}
            >
                {isHidden ? <Eye size={14} /> : <EyeOff size={14} />}
            </button>
        </div>

        {/* Image */}
        <div className="w-24 h-24 bg-slate-100 rounded-md overflow-hidden shrink-0 mt-4">
            {(item.image || item.imageNotAvailable) ? (
                item.imageNotAvailable ? (
                    <div className="w-full h-full flex items-center justify-center bg-slate-50 text-slate-300 text-[10px] font-bold text-center p-1">
                        NO IMAGE
                    </div>
                ) : (
                    <img src={item.image} alt={getText(item.name)} className="w-full h-full object-cover" />
                )
            ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-300">
                    <ShoppingBag size={24} />
                </div>
            )}
        </div>

        {/* Info */}
        <div className="flex-1 flex flex-col justify-center mt-4">
            <h4 className="font-bold text-slate-800 text-lg mb-1">{getText(item.name)}</h4>
            <p className="text-xs text-slate-500 line-clamp-2 mb-2 leading-relaxed">{getText(item.description) || (isArabic ? 'لا يوجد وصف.' : 'No description available.')}</p>
            
            {/* Item Note Chip */}
            {item.note && (item.note.en || item.note.ar) && (
                <div className="mb-2">
                    <span className="inline-block bg-yellow-100 text-yellow-800 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide border border-yellow-200">
                        {getText(item.note)}
                    </span>
                </div>
            )}

            <div className="flex items-center gap-2">
                <div className="text-orange-600 font-medium text-sm">
                    {item.price > 0 ? `${currency} ${item.price.toFixed(2)}` : (isArabic ? 'السعر عند الاختيار' : 'Price on Selection')}
                </div>
                {item.choiceGroups.length > 0 && (
                    <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">
                        {isArabic ? 'قابل للتخصيص' : 'Customizable'}
                    </span>
                )}
            </div>
        </div>

        {/* Add Button */}
        <div className="flex flex-col justify-end">
            <button 
                onClick={() => onSelect(item)}
                className="w-8 h-8 bg-orange-500 text-white rounded-full flex items-center justify-center hover:bg-orange-600 transition-colors shadow-sm active:scale-95"
            >
                <Plus size={18} strokeWidth={3} />
            </button>
        </div>
    </div>
  );
};

// Sortable Sidebar Category Component
const SidebarCategoryItem = ({ category, activeId, onClick, getText }: any) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: `sidebar-${category.id}`, data: { type: 'sidebar-category', originalId: category.id } });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 50 : 'auto',
    };

    return (
        <div ref={setNodeRef} style={style} className="mb-1">
            <div 
                className={`flex items-center justify-between w-full text-left px-2 py-2 text-sm rounded-lg transition-colors group ${
                    activeId === category.id 
                    ? 'bg-orange-50 text-orange-600 font-bold' 
                    : 'text-slate-600 hover:bg-white hover:text-orange-600'
                }`}
            >
                <button 
                    onClick={onClick}
                    className="flex-1 text-left truncate"
                >
                    {getText(category.name)}
                </button>
                <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 text-slate-300 hover:text-slate-500 opacity-0 group-hover:opacity-100 transition-opacity">
                    <GripVertical size={14} />
                </div>
            </div>
        </div>
    );
};

export const OrderingPage = forwardRef<OrderingPageRef, OrderingPageProps>(({ data, onBack, onEditItem, currency, language, viewFilters: propViewFilters, onOpenImageEditor }, ref) => {
  // Local State for "Playground" modifications
  const [localData, setLocalData] = useState<MenuData>(JSON.parse(JSON.stringify(data)));
  const [hiddenItemIds, setHiddenItemIds] = useState<string[]>([]);
  
  const showImagesOnly = propViewFilters?.showImagesOnly ?? false;
  const showNotesOnly = propViewFilters?.showNotesOnly ?? false;
  const showIncompleteOnly = propViewFilters?.showIncompleteOnly ?? false;

  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [activeCategoryId, setActiveCategoryId] = useState<string>('');
  
  const [cart, setCart] = useState<CartItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<MenuItem | null>(null);
  const [tempOptions, setTempOptions] = useState<SelectedOption[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedCategoryIds, setExpandedCategoryIds] = useState<string[]>(data.categories.map(c => c.id));

  const isArabic = language === 'ar';
  const getText = (obj: any) => isArabic ? (obj.ar || obj.en) : obj.en;
  const getDir = () => isArabic ? 'rtl' : 'ltr';
  const getFont = () => isArabic ? 'font-arabic' : 'font-sans';

  // Reset local data when props change (optional, or manual reset)
  const handleReset = async () => {
      if(window.confirm("Reset all changes in Live View? This will restore deleted items and order.")) {
          const freshData = JSON.parse(JSON.stringify(data));
          setLocalData(freshData);
          setHiddenItemIds([]);
      }
  };

  const toggleHideItem = (itemId: string) => {
      setHiddenItemIds(prev => 
          prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]
      );
  };

  const toggleCategory = (catId: string) => {
      setExpandedCategoryIds(prev => 
          prev.includes(catId) ? prev.filter(id => id !== catId) : [...prev, catId]
      );
  };

  const getExportData = () => {
      const exportData = JSON.parse(JSON.stringify(localData));
      exportData.categories = exportData.categories.map((c: any) => ({
          ...c,
          items: c.items.filter((item: any) => {
              // 1. Hidden Items
              if (hiddenItemIds.includes(item.id)) return false;

              // 2. View Filters
              const matchesImage = showImagesOnly ? (item.image && !item.imageNotAvailable) : true;
              const matchesNote = showNotesOnly ? (item.note && (item.note.en || item.note.ar)) : true;
              const matchesIncomplete = showIncompleteOnly ? (validateItem(item).length > 0) : true;

              return matchesImage && matchesNote && matchesIncomplete;
          })
      })).filter((c: any) => c.items.length > 0);
      return exportData;
  };

  const handleExportLive = () => {
      setIsExportModalOpen(true);
  };

  useImperativeHandle(ref, () => ({
      reset: handleReset,
      exportLive: handleExportLive
  }));

  // Scroll Spy Logic
  useEffect(() => {
      const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
              if (entry.isIntersecting) {
                  // Extract ID from "menu-cat-ID"
                  const id = entry.target.id.replace('menu-cat-', '');
                  setActiveCategoryId(id);
              }
          });
      }, {
          rootMargin: '-20% 0px -60% 0px', // Trigger when element is near top
          threshold: 0
      });

      localData.categories.forEach(cat => {
          const el = document.getElementById(`menu-cat-${cat.id}`);
          if (el) observer.observe(el);
      });

      return () => observer.disconnect();
  }, [localData.categories]);

  // DnD Sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragOver = (event: any) => {
    const { active, over } = event;
    if (!over) return;

    const activeId = active.id;
    const overId = over.id;

    // Only handle item dragging here
    if (active.data.current?.type !== 'item') return;

    // Find containers
    const activeCat = localData.categories.find(c => c.items.some(i => i.id === activeId));
    const overCat = localData.categories.find(c => c.items.some(i => i.id === overId)) || 
                    localData.categories.find(c => c.id === overId); // Dropping on category header

    if (!activeCat || !overCat || activeCat.id === overCat.id) return;

    setLocalData(prev => {
        const activeItems = activeCat.items;
        const overItems = overCat.items;
        const activeIndex = activeItems.findIndex(i => i.id === activeId);
        const overIndex = overItems.findIndex(i => i.id === overId);

        let newIndex;
        if (overId === overCat.id) {
            newIndex = overItems.length + 1;
        } else {
            const isBelowOverItem =
              over &&
              active.rect.current.translated &&
              active.rect.current.translated.top > over.rect.top + over.rect.height;

            const modifier = isBelowOverItem ? 1 : 0;
            newIndex = overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
        }

        return {
            ...prev,
            categories: prev.categories.map(c => {
                if (c.id === activeCat.id) {
                    return { ...c, items: c.items.filter(i => i.id !== activeId) };
                }
                if (c.id === overCat.id) {
                    const newItems = [...c.items];
                    const itemMoved = activeItems[activeIndex];
                    if (itemMoved) {
                        // Ensure no duplicates
                        if (!newItems.find(i => i.id === itemMoved.id)) {
                            newItems.splice(newIndex, 0, itemMoved);
                        }
                    }
                    return { ...c, items: newItems };
                }
                return c;
            })
        };
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    // Handle Sidebar Category Reordering
    if (active.data.current?.type === 'sidebar-category') {
        if (active.id !== over.id) {
            const oldIndex = localData.categories.findIndex(c => `sidebar-${c.id}` === active.id);
            const newIndex = localData.categories.findIndex(c => `sidebar-${c.id}` === over.id);
            if (oldIndex !== -1 && newIndex !== -1) {
                setLocalData(prev => ({
                    ...prev,
                    categories: arrayMove(prev.categories, oldIndex, newIndex)
                }));
            }
        }
        return;
    }

    // Handle Category Reordering (Main View)
    if (active.data.current?.type === 'category') {
        if (active.id !== over.id) {
            const oldIndex = localData.categories.findIndex(c => c.id === active.id);
            const newIndex = localData.categories.findIndex(c => c.id === over.id);
            setLocalData(prev => ({
                ...prev,
                categories: arrayMove(prev.categories, oldIndex, newIndex)
            }));
        }
        return;
    }

    // Handle Item Reordering (Same Category)
    const activeCat = localData.categories.find(c => c.items.some(i => i.id === active.id));
    const overCat = localData.categories.find(c => c.items.some(i => i.id === over.id));

    if (activeCat && overCat && activeCat.id === overCat.id) {
        const oldIndex = activeCat.items.findIndex(i => i.id === active.id);
        const newIndex = activeCat.items.findIndex(i => i.id === over.id);
        
        if (oldIndex !== newIndex) {
            setLocalData(prev => ({
                ...prev,
                categories: prev.categories.map(c => c.id === activeCat.id ? {
                    ...c,
                    items: arrayMove(c.items, oldIndex, newIndex)
                } : c)
            }));
        }
    }
  };

  // -- Cart Logic --
  const addToCart = () => {
    if (!selectedItem) return;
    const unitPrice = selectedItem.price + tempOptions.reduce((acc, opt) => acc + opt.price, 0);
    const newItem: CartItem = {
      id: `cart-${Date.now()}-${Math.random()}`,
      menuItem: selectedItem,
      selectedOptions: tempOptions,
      quantity: 1,
      unitPrice: unitPrice,
      totalPrice: unitPrice
    };
    setCart(prev => [...prev, newItem]);
    setSelectedItem(null);
    setTempOptions([]);
  };

  const removeFromCart = (id: string) => {
    setCart(prev => prev.filter(item => item.id !== id));
  };

  const updateQuantity = (id: string, delta: number) => {
    setCart(prev => prev.map(item => {
      if (item.id === id) {
        const newQty = Math.max(1, item.quantity + delta);
        return { ...item, quantity: newQty, totalPrice: item.unitPrice * newQty };
      }
      return item;
    }));
  };

  const cartTotal = cart.reduce((acc, item) => acc + item.totalPrice, 0);

  // -- Modal Logic --
  const toggleOption = (group: ChoiceGroup, optionId: string, price: number, name: any) => {
    const existingIdx = tempOptions.findIndex(o => o.optionId === optionId);
    const groupOptions = tempOptions.filter(o => o.groupId === group.id);

    if (existingIdx > -1) {
      setTempOptions(tempOptions.filter(o => o.optionId !== optionId));
    } else {
      if (group.max === 1) {
        const otherOptions = tempOptions.filter(o => o.groupId !== group.id);
        setTempOptions([...otherOptions, { groupId: group.id, optionId, price, name }]);
      } else {
        if (group.max === 0 || groupOptions.length < group.max) {
          setTempOptions([...tempOptions, { groupId: group.id, optionId, price, name }]);
        }
      }
    }
  };

  const isOptionSelected = (optId: string) => tempOptions.some(o => o.optionId === optId);

  const isValidOrder = () => {
    if (!selectedItem) return false;
    for (const group of (selectedItem.choiceGroups || [])) {
      const selectedCount = tempOptions.filter(o => o.groupId === group.id).length;
      const effectiveMin = group.required ? Math.max(1, group.min) : group.min;
      if (group.required && selectedCount === 0) return false;
      if (group.min > 0 && selectedCount < group.min) return false;
    }
    return true;
  };

  // Filter Data for Display
  const filteredCategories = localData.categories.map(cat => ({
    ...cat,
    items: cat.items.filter(item => {
        const matchesSearch = (getText(item.name) || '').toLowerCase().includes(searchTerm.toLowerCase());
        const matchesImage = showImagesOnly ? (item.image && !item.imageNotAvailable) : true;
        const matchesNote = showNotesOnly ? (item.note && (item.note.en || item.note.ar)) : true;
        const matchesIncomplete = showIncompleteOnly ? (validateItem(item).length > 0) : true;
        return matchesSearch && matchesImage && matchesNote && matchesIncomplete;
    })
  })).filter(cat => cat.items.length > 0);

  const currentPrice = selectedItem ? (selectedItem.price + tempOptions.reduce((acc, opt) => acc + opt.price, 0)) : 0;

  return (
    <div className={`min-h-screen bg-[#F7F7F7] text-slate-800 ${getFont()}`} dir={getDir()}>
      
      {/* Restaurant Header Info */}
      <div className="bg-white border-b border-slate-200 pb-0 pt-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row gap-6 mb-6">
                {/* Logo */}
                <div className="w-24 h-24 bg-white rounded-full flex items-center justify-center text-slate-900 shrink-0 shadow-lg overflow-hidden border border-slate-100">
                    {localData.logo ? (
                        <img src={localData.logo} alt="Logo" className="w-full h-full object-cover" />
                    ) : (
                        <span className="text-2xl font-bold">HDM</span>
                    )}
                </div>
                
                {/* Info */}
                <div className="flex-1">
                    <h1 className="text-3xl font-black text-slate-900 mb-2">{getText(localData.title)}</h1>
                    <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                        <span className="flex items-center gap-1"><Star size={14} className="text-orange-500 fill-orange-500" /> 4.8 (500+)</span>
                        <span className="flex items-center gap-1"><Clock size={14} /> 30-45 min</span>
                        <span className="flex items-center gap-1"><MapPin size={14} /> 1.2 km away</span>
                        <span className="flex items-center gap-1"><CreditCard size={14} /> Min. order {currency} 50</span>
                    </div>
                    <div className="mt-4 flex gap-2">
                        <button className="px-4 py-1.5 bg-slate-100 text-slate-700 rounded-full text-sm font-bold hover:bg-slate-200 transition-colors flex items-center gap-2">
                            <Info size={14} /> {isArabic ? 'معلومات' : 'Info'}
                        </button>
                        <button className="px-4 py-1.5 bg-slate-100 text-slate-700 rounded-full text-sm font-bold hover:bg-slate-200 transition-colors flex items-center gap-2">
                            <MessageSquare size={14} /> {isArabic ? 'تقييمات' : 'Reviews'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Category Tabs */}
            <div className="flex gap-6 overflow-x-auto pb-0 scrollbar-hide">
                {filteredCategories.map(cat => (
                    <button 
                        key={cat.id} 
                        onClick={() => {
                            document.getElementById(`menu-cat-${cat.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }}
                        className={`pb-4 text-sm font-bold border-b-2 whitespace-nowrap transition-colors ${
                            activeCategoryId === cat.id 
                            ? 'text-orange-600 border-orange-600' 
                            : 'text-slate-500 hover:text-orange-600 border-transparent hover:border-orange-600'
                        }`}
                    >
                        {getText(cat.name)}
                    </button>
                ))}
            </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <DndContext 
            sensors={sensors} 
            collisionDetection={closestCenter} 
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
        >
        <div className="grid grid-cols-12 gap-8">
            
            {/* LEFT SIDEBAR: Categories (Desktop) */}
            <div className="hidden lg:block col-span-2">
                <div className="sticky top-24 space-y-1">
                    <h3 className="font-bold text-slate-900 mb-4 px-2">{isArabic ? 'الأقسام' : 'Categories'}</h3>
                    <SortableContext 
                        items={filteredCategories.map(c => `sidebar-${c.id}`)} 
                        strategy={verticalListSortingStrategy}
                    >
                        {filteredCategories.map(cat => (
                            <SidebarCategoryItem 
                                key={cat.id} 
                                category={cat} 
                                activeId={activeCategoryId}
                                getText={getText}
                                onClick={() => {
                                    document.getElementById(`menu-cat-${cat.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                }}
                            />
                        ))}
                    </SortableContext>
                </div>
            </div>

            {/* MIDDLE: Menu Items */}
            <div className="col-span-12 lg:col-span-7">
                {/* Search */}
                <div className="bg-white p-2 rounded-lg shadow-sm border border-slate-200 mb-6 flex items-center gap-2">
                    <Search className="ml-2 text-slate-400" size={20} />
                    <input 
                        type="text" 
                        placeholder={isArabic ? "بحث في القائمة..." : "Search menu item"} 
                        className="flex-1 outline-none text-sm p-2"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                    />
                </div>

                {/* Categories & Items */}
                <SortableContext 
                    items={filteredCategories.map(c => c.id)} 
                    strategy={verticalListSortingStrategy}
                >
                    <div className="space-y-6">
                            {filteredCategories.map(cat => (
                                <SortableCategory 
                                    key={cat.id} 
                                    category={cat} 
                                    getText={getText} 
                                    isArabic={isArabic}
                                    isExpanded={expandedCategoryIds.includes(cat.id)}
                                    onToggle={() => toggleCategory(cat.id)}
                                >
                                    <SortableContext 
                                        items={cat.items.map(i => i.id)} 
                                        strategy={rectSortingStrategy}
                                    >
                                        <div className="space-y-4 min-h-[50px]">
                                            {cat.items.map(item => (
                                                <SortableItemCard 
                                                    key={item.id} 
                                                    item={item} 
                                                    getText={getText} 
                                                    currency={currency} 
                                                    isArabic={isArabic}
                                                    onSelect={hiddenItemIds.includes(item.id) ? () => {} : setSelectedItem}
                                                    onEdit={onEditItem}
                                                    onToggleHide={toggleHideItem}
                                                    isHidden={hiddenItemIds.includes(item.id)}
                                                    showIncompleteOnly={showIncompleteOnly}
                                                />
                                            ))}
                                            {cat.items.length === 0 && (
                                                <div className="text-center py-4 text-slate-400 text-sm border-2 border-dashed border-slate-200 rounded-lg">
                                                    Drop items here
                                                </div>
                                            )}
                                        </div>
                                    </SortableContext>
                                </SortableCategory>
                            ))}
                        </div>
                    </SortableContext>
            </div>

            {/* RIGHT SIDEBAR: Cart */}
            <div className="col-span-12 lg:col-span-3">
                <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden sticky top-24">
                    {/* Cart Header */}
                    <div className="bg-[#EF5305] text-white p-4 font-bold text-lg">
                        {isArabic ? 'سلة المشتريات' : 'Your Cart'}
                    </div>

                    <div className="min-h-[200px] max-h-[calc(100vh-200px)] overflow-y-auto bg-[#F9F9F9]">
                        {cart.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-64 text-slate-400">
                                <ShoppingBag size={64} className="mb-4 opacity-20" strokeWidth={1} />
                                <p className="font-medium text-slate-500">{isArabic ? 'السلة فارغة' : 'There are no items in your cart'}</p>
                            </div>
                        ) : (
                            <div className="p-4 space-y-4">
                                {cart.map(item => (
                                    <div key={item.id} className="bg-white p-3 rounded border border-slate-200 shadow-sm relative group">
                                        {/* Delete Icon */}
                                        <DeleteConfirmButton 
                                            onDelete={() => removeFromCart(item.id)}
                                            className={`absolute top-2 ${isArabic ? 'left-2' : 'right-2'} p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors z-10`}
                                            icon={<X size={16} />}
                                        />

                                        <div className={`text-sm font-bold text-slate-800 ${isArabic ? 'pl-6' : 'pr-6'}`}>{getText(item.menuItem.name)}</div>
                                        {item.selectedOptions.length > 0 && (
                                            <div className="text-xs text-slate-500 mt-1">
                                                {item.selectedOptions.map(o => getText(o.name)).join(', ')}
                                            </div>
                                        )}
                                        <div className="flex items-center justify-between mt-3">
                                            <div className="text-sm font-bold text-slate-700">
                                                {currency} {item.totalPrice.toFixed(2)}
                                            </div>
                                            <div className="flex items-center gap-2 bg-slate-100 rounded px-1">
                                                <button onClick={() => updateQuantity(item.id, -1)} className="p-1 hover:text-orange-600"><Minus size={12}/></button>
                                                <span className="text-xs font-bold w-3 text-center">{item.quantity}</span>
                                                <button onClick={() => updateQuantity(item.id, 1)} className="p-1 hover:text-orange-600"><Plus size={12}/></button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {cart.length > 0 && (
                        <div className="p-4 bg-white border-t border-slate-200">
                            <div className="flex justify-between items-center mb-4 text-slate-800">
                                <span className="font-bold">{isArabic ? 'المجموع' : 'Total'}</span>
                                <span className="font-bold text-xl">{currency} {cartTotal.toFixed(2)}</span>
                            </div>
                            <button className="w-full py-3 bg-[#EF5305] hover:bg-[#d14400] text-white font-bold rounded shadow-sm transition-colors">
                                {isArabic ? 'إتمام الطلب' : 'Checkout'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
        </DndContext>
      </div>

      {/* Customization Modal */}
      {selectedItem && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Modal Header with Image */}
            <div className="relative h-48 bg-slate-100 shrink-0">
                {(selectedItem.image || selectedItem.imageNotAvailable) ? (
                    selectedItem.imageNotAvailable ? (
                        <div className="w-full h-full flex items-center justify-center bg-slate-200 text-slate-400 font-bold">
                            NO IMAGE
                        </div>
                    ) : (
                        <img src={selectedItem.image} className="w-full h-full object-cover" alt="" />
                    )
                ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300">
                        <ShoppingBag size={48} />
                    </div>
                )}
                <button 
                    onClick={() => { setSelectedItem(null); setTempOptions([]); }}
                    className={`absolute top-4 ${isArabic ? 'left-4' : 'right-4'} bg-white p-2 rounded-full text-slate-900 hover:bg-slate-100 transition-colors shadow-sm`}
                >
                    <X size={20} />
                </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
                <div className="mb-6 border-b border-slate-100 pb-4">
                    <div className="flex justify-between items-start">
                        <h2 className="text-2xl font-bold text-slate-900 mb-1">{getText(selectedItem.name)}</h2>
                        <span className="font-bold text-lg text-orange-600">{currency} {selectedItem.price.toFixed(2)}</span>
                    </div>
                    <p className="text-slate-500 text-sm leading-relaxed mb-2">{getText(selectedItem.description)}</p>
                    
                    {/* Item Note Chip in Modal */}
                    {selectedItem.note && (selectedItem.note.en || selectedItem.note.ar) && (
                        <div className="mb-2">
                            <span className="inline-block bg-yellow-100 text-yellow-800 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide border border-yellow-200">
                                {getText(selectedItem.note)}
                            </span>
                        </div>
                    )}
                </div>

                <div className="space-y-6">
                    {(selectedItem.choiceGroups || []).length > 0 ? (
                        selectedItem.choiceGroups.map(group => {
                            const currentSelections = tempOptions.filter(o => o.groupId === group.id);
                            const count = currentSelections.length;
                            // Strict check logic
                            const effectiveMin = group.required ? Math.max(1, group.min) : group.min;
                            const isSatisfied = count >= effectiveMin && (group.max === 0 || count <= group.max);
                            
                            return (
                                <div key={group.id} className="bg-slate-50 p-4 rounded-lg border border-slate-100">
                                    <div className="flex justify-between items-baseline mb-3">
                                        <h3 className="font-bold text-slate-800">{getText(group.name) || 'Options'}</h3>
                                        <div className="text-xs">
                                            {group.required && <span className={`text-white font-bold bg-orange-500 px-2 py-0.5 rounded ${isArabic ? 'ml-2' : 'mr-2'}`}>{isArabic ? 'مطلوب' : 'Required'}</span>}
                                            <span className="text-slate-400">
                                                {group.max === 1 ? (isArabic ? 'اختر 1' : 'Select 1') : (group.max > 1 ? (isArabic ? `حد أقصى ${group.max}` : `Max ${group.max}`) : '')}
                                            </span>
                                        </div>
                                    </div>
                                    
                                    <div className="space-y-2">
                                        {(group.options || []).map(opt => {
                                            const isSelected = isOptionSelected(opt.id);
                                            const isDisabled = !isSelected && group.max > 0 && count >= group.max && group.max !== 1;

                                            return (
                                                <label 
                                                    key={opt.id}
                                                    className={`flex items-center justify-between p-3 rounded border cursor-pointer transition-all bg-white ${
                                                        isSelected 
                                                        ? 'border-orange-500 ring-1 ring-orange-500' 
                                                        : 'border-slate-200 hover:border-orange-300'
                                                    } ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-5 h-5 rounded-full border flex items-center justify-center ${
                                                            isSelected ? 'border-orange-500 bg-orange-500 text-white' : 'border-slate-300 bg-white'
                                                        }`}>
                                                            {isSelected && <Check size={12} strokeWidth={3} />}
                                                        </div>
                                                        <span className="font-medium text-sm text-slate-700">{getText(opt.name)}</span>
                                                    </div>
                                                    {opt.priceModifier ? (
                                                        <span className="text-sm font-medium text-slate-500">
                                                            +{currency} {opt.priceModifier.toFixed(2)}
                                                        </span>
                                                    ) : null}
                                                    
                                                    <input 
                                                        type="checkbox" 
                                                        className="hidden" 
                                                        checked={isSelected}
                                                        disabled={isDisabled}
                                                        onChange={() => toggleOption(group, opt.id, opt.priceModifier || 0, opt.name)}
                                                    />
                                                </label>
                                            );
                                        })}
                                    </div>
                                    {!isSatisfied && (
                                        <p className="text-xs text-red-500 mt-2 flex items-center gap-1 font-medium">
                                            <AlertCircle size={12} /> 
                                            {group.required && count === 0 ? (isArabic ? "الاختيار مطلوب" : "Selection required") : (isArabic ? `اختر على الأقل ${effectiveMin}` : `Select at least ${effectiveMin}`)}
                                        </p>
                                    )}
                                </div>
                            );
                        })
                    ) : (
                        <div className="text-center py-8 text-slate-400 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                            <p className="text-sm">{isArabic ? 'لا توجد خيارات إضافية لهذا الطبق' : 'No customization options available for this item'}</p>
                        </div>
                    )}
                </div>

                <div className="mt-6 pt-4 border-t border-slate-100 flex justify-between items-center">
                    <div className="text-xl font-bold text-slate-900">
                        {currency} {currentPrice.toFixed(2)}
                    </div>
                    <button 
                        onClick={addToCart}
                        disabled={!isValidOrder()}
                        className={`px-8 py-3 rounded-lg font-bold text-white transition-all ${
                            isValidOrder() 
                            ? 'bg-[#EF5305] hover:bg-[#d14400] shadow-lg active:scale-95' 
                            : 'bg-slate-300 cursor-not-allowed'
                        }`}
                    >
                        {isArabic ? 'أضف للسلة' : 'Add to Cart'}
                    </button>
                </div>
            </div>
          </div>
        </div>
      )}
      <ExportModal 
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExportTemplate1={() => {
          exportTemplate1(getExportData(), `${localData.title.en || 'Menu'} (Live View)`, currency);
          setIsExportModalOpen(false);
        }}
        onExportTemplate2={() => {
          exportTemplate2(getExportData(), `${localData.title.en || 'Menu'} (Live View)`, currency);
          setIsExportModalOpen(false);
        }}
        onExportBoth={() => {
          exportTemplateBoth(getExportData(), `${localData.title.en || 'Menu'} (Live View)`, currency);
          setIsExportModalOpen(false);
        }}
        onOpenImageEditor={() => {
            if (onOpenImageEditor) {
                onOpenImageEditor();
                setIsExportModalOpen(false);
            }
        }}
        data={getExportData()}
      />
    </div>
  );
});